import React from 'react';
import useAsyncDataCourier from '../../common/custom-hooks/useAsyncDataCourier';
import MessageCourier from '../../lib/MessageCourier';
import { Entrenador } from '../../system/Entrenador/Entrenador';
import SystemClient from '../../system/SystemClient';
import ComboBoxSearch, { IComboBoxSearch } from '../Common/ComboBoxSearch';
import { IComboBoxOption } from '@fluentui/react';
import ComboBoxInput from '../../lib/FormValidator/Form/ComboBoxInput';
import CustomInputIfc from '../../lib/FormValidator/Form/CustomInputIfc';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import useForceUpdate from '../../common/custom-hooks/useForceUpdate';

interface IEntrenadorSelector {
  system: SystemClient;
  courier: MessageCourier;
  onSelect: (entrenador: Entrenador) => void;
  selected: string[];
  providedList?: Entrenador[];
  noShowDocumentNumber?: boolean;
  clearOnSelect?: boolean;
  disabled?: boolean;
}

//==========================================================
// Selector independiente
//==========================================================
export const EntrenadorSelector = (props: IEntrenadorSelector) => {
  const entrenadores = useAsyncDataCourier(
    () =>
      props.providedList
        ? Promise.resolve(props.providedList)
        : props.system.getEntrenadorSystem().getListForDropdown(),
    [],
    props.courier
  );
  const entrenadorOptions = React.useMemo(() => {
    let ret: IComboBoxOption[] = [];
    if (entrenadores) {
      ret = entrenadores.map((entity: Entrenador) => {
        return { key: entity.getId()!, text: `${entity.getApellido()}, ${entity.getNombre()}`, data: entity };
      });
    }
    return ret;
  }, [entrenadores]);

  const getFromKey = (key: string) => {
    if (entrenadores) {
      const fed = entrenadores.find((f) => f.getId() == key);
      if (fed) {
        return props.onSelect(fed);
      }
    }
  };

  return (
    <ComboBoxSearch
      onSelectOption={(opt) => getFromKey(String(opt.key))}
      selected={props.selected}
      options={entrenadorOptions}
      cleanOnSelect={props.clearOnSelect}
      disabled={props.disabled}
    ></ComboBoxSearch>
  );
};

//==========================================================
// Selector Entrenador, integrado con formulario EntrenadorSelectorInput
//==========================================================

interface IEntrenadorSelectorInput {
  system: SystemClient;
  courier: MessageCourier;
  input: CustomInputIfc;
  hideLabel?: boolean;
  onBlurCustomInput: (customInput: CustomInputIfc) => void;
  useDefault?: () => string | null;
  disabled?: boolean;
  noShowDocumentNumber?: boolean;
}

export const EntrenadorSelectorInput = (props: IEntrenadorSelectorInput) => {
  const { t, i18n } = useTranslation();
  const [entrenador, setCurrentEntrenador] = React.useState<Entrenador | undefined>(undefined);
  const [entrenadorOptions, setEntrenadorOptions] = React.useState<IComboBoxOption[]>([]);
  const entrenadores = useAsyncDataCourier(
    () => props.system.getEntrenadorSystem().getListForDropdown(),
    [],
    props.courier
  );

  //==========================================================
  // Definimos key and label
  //==========================================================
  const mapEntrenadorOptions = (entrenadores: Entrenador[]) => {
    const opciones = entrenadores.map((entity: Entrenador) => {
      return { key: entity.getId()!, text: getLabelFromEntrenador(entity), data: entity };
    });
    setEntrenadorOptions(opciones);
  };

  //==========================================================
  // Define valor por defecto (input)
  //==========================================================
  const selectDefaultEntrenador = (id: string) => {
    setCurrentEntrenador(
      entrenadores ? entrenadores.find((entity: Entrenador) => entity.getId() === id) : undefined
    );
  };

  const forceUpdate = useForceUpdate();

  //==========================================================
  // Define custom label ( Entrenador + Federación)
  //==========================================================
  const getLabelFromEntrenador = (entrenador: Entrenador) => {
    let extra = props.noShowDocumentNumber ? '' : `(Doc: ${entrenador.getDocumentoNumero()})`;
    const label = `${entrenador.getApellido()}, ${entrenador.getNombre()} ${extra}`;

    return label;
  };

  React.useEffect(() => {
    forceUpdate();
  }, [entrenador]);

  React.useEffect(() => {
    selectDefaultEntrenador(props.input.getValue());
    mapEntrenadorOptions(entrenadores ? entrenadores : []);
  }, [entrenadores, entrenador]);

  return (
    <>
      {entrenadores && (
        <ComboBoxInput
          input={props.input}
          label={props.hideLabel ? '' : t('Entrenador (*)')}
          onBlur={props.onBlurCustomInput}
          options={entrenadorOptions ? entrenadorOptions : []}
          selectedKey={props.input.getValue()}
          disabled={props.disabled}
        />
      )}
    </>
  );
};
